import PropTypes from "prop-types"
import React from "react"
import { Box, TextField } from "@mui/material"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { ptBR as ptBRDatePickers } from "@mui/x-date-pickers"
import ptBR from "date-fns/locale/pt-BR"
import { tableTokens } from "../../components/Common/table-theme"
import { removeHour } from "../../helpers/utils"

const ptBRLocale = ptBRDatePickers.components.MuiLocalizationProvider.defaultProps.localeText

/**
 * Células e formatações compartilhadas pelos grids de detalhe da venda (itens e insumos).
 */

export const describeQuantidade = valor =>
    parseFloat(valor || 0).toLocaleString("pt-br", { maximumFractionDigits: 3 })

/**
 * Situação dos registros de detalhe. Registro cancelado permanece consultável e é
 * identificado pela cor. Valor fora da lista é exibido cru, sem inventar significado.
 */
export const SITUACOES_DETALHE = {
    A: { chave: "movimentovendas.detalhe.situacao.a", classe: "badge-soft-success" },
    V: { chave: "movimentovendas.detalhe.situacao.v", classe: "badge-soft-primary" },
    C: { chave: "movimentovendas.detalhe.situacao.c", classe: "badge-soft-danger" },
    I: { chave: "movimentovendas.detalhe.situacao.i", classe: "badge-soft-warning" },
    T: { chave: "movimentovendas.detalhe.situacao.t", classe: "badge-soft-info" },
    P: { chave: "movimentovendas.detalhe.situacao.p", classe: "badge-soft-secondary" }
}

export const SituacaoBadge = ({ situacao, t }) => {
    if (!situacao) {
        return "-"
    }

    const definicao = SITUACOES_DETALHE[situacao]
    return (
        <span className={ `badge ${ definicao ? definicao.classe : "badge-soft-secondary" }` }>
            { definicao ? t(definicao.chave) : situacao }
        </span>
    )
}

SituacaoBadge.propTypes = {
    situacao: PropTypes.string,
    t: PropTypes.func
}

/**
 * Marcação S/N das colunas char(1) dos insumos.
 */
export const describeSimNao = (valor, t) => {
    if (!valor) {
        return "-"
    }

    switch (valor.toUpperCase()) {
        case "S":
            return t("movimentovendas.detalhe.sim")
        case "N":
            return t("movimentovendas.detalhe.nao")
        default:
            return valor
    }
}

/**
 * Mensagem de grid vazio. Repete a moldura do fallback padrão da tabela para o detalhe
 * poder dar um texto específico sem destoar visualmente.
 */
export const EmptyRowsMessage = ({ mensagem }) => (
    <Box
        sx={ {
            position: "sticky",
            left: 0,
            width: "100%",
            textAlign: "center",
            padding: "0.5rem",
            color: tableTokens.colorMuted,
            fontSize: tableTokens.fontSizeUi,
            fontFamily: tableTokens.fontFamily
        } }
    >
        { mensagem }
    </Box>
)

EmptyRowsMessage.propTypes = {
    mensagem: PropTypes.string
}

/**
 * Filtro de coluna de data, no mesmo formato das demais telas: a data escolhida vira
 * um intervalo do dia inteiro no backend.
 */
export const DateColumnFilter = ({ column }) => (
    <LocalizationProvider dateAdapter={ AdapterDateFns } adapterLocale={ ptBR } localeText={ ptBRLocale }>
        <DatePicker
            onChange={ novoValor => column.setFilterValue(novoValor ? removeHour(novoValor, false) : novoValor) }
            inputFormat="dd/MM/yyyy"
            componentsProps={ { actionBar: { actions: ["accept", "clear"] } } }
            value={ column.getFilterValue() ? new Date(column.getFilterValue()) : null }
            views={ ["year", "month", "day"] }
            renderInput={ props => <TextField { ...props } variant="standard" /> }
        />
    </LocalizationProvider>
)

DateColumnFilter.propTypes = {
    column: PropTypes.object
}

/**
 * Opções dos filtros de coluna com domínio fechado.
 */
export const opcoesSituacao = t => Object.keys(SITUACOES_DETALHE)
    .map(situacao => ({ text: t(SITUACOES_DETALHE[situacao].chave), value: situacao }))

export const opcoesSimNao = t => [
    { text: t("movimentovendas.detalhe.sim"), value: "S" },
    { text: t("movimentovendas.detalhe.nao"), value: "N" }
]
